import React from 'react';
import { MessageSquare, Eye, Hammer, ShieldCheck } from 'lucide-react';

const ProcessStep = ({ number, title, description, icon: IconComponent }) => (
  <div className="process-step">
    {/* The number badge at the top right */}
    <div className="number-badge">
      <span>{number}</span>
    </div>

    {/* Icon Container */}
    <div className="icon-container">
      <IconComponent className="icon" />
    </div>

    {/* Title */}
    <h3 className="step-title">
      {title}
    </h3>

    {/* Description */}
    <p className="step-description">
      {description}
    </p>
  </div>
);

const App = () => {
  return (
    <>
      <style>
        {`
        .main-container {
          min-height: 100vh;
          background-color: #f9fafb;
          padding-top: 4rem;
          padding-bottom: 4rem;
          margin-inline:5%;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
        }

        @media (min-width: 640px) {
          .main-container {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        
        @media (min-width: 1024px) {
          .main-container {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        .content-wrapper {
          max-width: 80rem;
          margin-left: auto;
          margin-right: auto;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-title {
          font-size: 1.875rem;
          line-height: 2.25rem;
          font-weight: 700;
        color:#0A2472;

          margin-bottom: 1rem;
        }

        @media (min-width: 640px) {
          .section-title {
            font-size: 2.25rem;
            line-height: 2.5rem;
          }
        }
        
        @media (min-width: 768px) {
          .section-title {
            font-size: 3rem;
            line-height: 1;
          }
        }

        .section-subtitle {
          max-width: 42rem;
          margin-left: auto;
          margin-right: auto;
          font-size: 1rem;
          line-height: 1.5rem;
          color: #6b7280;
        }

        @media (min-width: 640px) {
          .section-subtitle {
            font-size: 1.125rem;
            line-height: 1.75rem;
          }
        }

        .process-grid {
          display: grid;
          grid-template-columns: repeat(1, minmax(0, 1fr));
          gap: 2rem;
        }

        @media (min-width: 640px) {
          .process-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        
        @media (min-width: 1024px) {
          .process-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }

        .process-step {
          position: relative;
          padding: 1.5rem;
          background-color: #fff;
          border-radius: 1rem;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          border: 1px solid #f3f4f6;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .process-step:hover {
          transform: scale(1.05);
        }

        .number-badge {
          position: absolute;
          top: 0;
          right: 0;
          margin: 1rem;
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          background-color: #dc2626;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 0.75rem;
          line-height: 1rem;
          font-weight: 700;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        @media (min-width: 768px) {
          .number-badge {
            width: 2.5rem;
            height: 2.5rem;
            font-size: 1rem;
            line-height: 1.5rem;
          }
        }

        .icon-container {
          width: 4rem;
          height: 4rem;
          background-color: #2563eb;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
        }

        @media (min-width: 768px) {
          .icon-container {
            width: 5rem;
            height: 5rem;
          }
        }

        .icon {
          width: 2rem;
          height: 2rem;
          color: #fff;
        }
        
        @media (min-width: 768px) {
          .icon {
            width: 2.5rem;
            height: 2.5rem;
          }
        }

        .step-title {
          font-size: 1.25rem;
          line-height: 1.75rem;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        @media (min-width: 768px) {
          .step-title {
            font-size: 1.5rem;
            line-height: 2rem;
          }
        }

        .step-description {
          font-size: 0.875rem;
          line-height: 1.25rem;
          color: #6b7280;
        }

        @media (min-width: 768px) {
          .step-description {
            font-size: 1rem;
            line-height: 1.5rem;
          }
        }
        `}
      </style>
      <div className="main-container">
        <div className="content-wrapper">
          {/* Section Title and Subtitle */}
          <div className="header-section">
            <h2 className="section-title">
              OUR PROVEN PROCESS
            </h2>
            <p className="section-subtitle">
              Four strategic phases designed for excellence, efficiency, and exceptional results in every painting and design project.
            </p>
          </div>

          {/* Process Steps Grid */}
          <div className="process-grid">
            <ProcessStep
              number="01"
              title="Initial Consultation"
              description="Comprehensive consultation to understand your vision, space requirements, and design preferences."
              icon={MessageSquare}
            />
            <ProcessStep
              number="02"
              title="Design & Planning"
              description="Detailed design proposal with color schemes, materials selection, and project timeline."
              icon={Eye}
            />
            <ProcessStep
              number="03"
              title="Expert Execution"
              description="Professional execution by skilled craftsmen with meticulous attention to detail."
              icon={Hammer}
            />
            <ProcessStep
              number="04"
              title="Quality Assurance"
              description="Thorough final inspection ensuring perfect finish and complete client satisfaction."
              icon={ShieldCheck}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default App;
